import * as THREE from 'three';

/**
 * Backdrops are small cartoon dioramas drawn in their own pass behind the tomato. They are seen from a
 * camera a little above the tomato's, so there is a real horizon and ground to stand on.
 */
export interface Backdrop {
    scene: THREE.Scene;
    /** Everything stands on y = GROUND_Y of this group, which follows the tomato's stage. */
    root: THREE.Group;
    /** True when the scene has no ground for the tomato's blob shadow to fall on. */
    hideShadow: boolean;
    /** Whether the scene has a ground plane (so the camera is raised to show it). */
    update: (time: number, dt: number) => void;
    dispose: () => void;
    /**
     * Called after the camera is aimed, whenever the view changes shape (resize, reserve), so a scene can
     * spread its things across the screen instead of assuming one aspect ratio.
     */
    fit?: (camera: THREE.PerspectiveCamera) => void;
    /** Swings slightly about the tomato following the pointer. */
    orbit?: boolean;
}

/**
 * The point of root-local depth `z` that lands on normalised screen position (nx, ny), each in -1..1
 * (x right, y up), as seen by `camera`.
 */
export const screenToRoot = (
    camera: THREE.PerspectiveCamera,
    root: THREE.Object3D,
    nx: number,
    ny: number,
    z: number,
    out = new THREE.Vector3()
): THREE.Vector3 => {
    const ray = new THREE.Vector3(nx, ny, 0.5)
        .unproject(camera)
        .sub(camera.position)
        .normalize();
    const planeZ = root.position.z + root.scale.z * z;
    const k = (planeZ - camera.position.z) / ray.z;
    out.copy(camera.position).addScaledVector(ray, k);
    return root.worldToLocal(out);
};

/** Where the tomato's feet touch the ground. */
export const GROUND_Y = -0.86;

export const rng = (seed: number) => {
    let a = seed >>> 0;
    return () => {
        a = (a + 0x6d2b79f5) | 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
};

let gradient: THREE.DataTexture | null = null;
/** Three flat shading steps: what makes lit surfaces look hand-inked instead of smooth. */
const toonGradient = (): THREE.DataTexture => {
    if (!gradient) {
        gradient = new THREE.DataTexture(
            new Uint8Array([105, 175, 255]),
            3,
            1,
            THREE.RedFormat
        );
        gradient.minFilter = THREE.NearestFilter;
        gradient.magFilter = THREE.NearestFilter;
        gradient.needsUpdate = true;
    }
    return gradient;
};

export const toon = (
    color: THREE.ColorRepresentation,
    extra: THREE.MeshToonMaterialParameters = {}
) =>
    new THREE.MeshToonMaterial({
        color,
        gradientMap: toonGradient(),
        ...extra,
    });

/** Unlit colour that ignores tone mapping, for the sun, glows and anything that should look painted. */
export const flat = (
    color: THREE.ColorRepresentation,
    extra: THREE.MeshBasicMaterialParameters = {}
) => new THREE.MeshBasicMaterial({ color, toneMapped: false, ...extra });

const INK = 0x2a1830;

/** A dark inflated copy behind the mesh: the cartoon outline. */
export const inked = <T extends THREE.Mesh>(mesh: T, thickness = 0.07): T => {
    const outline = new THREE.Mesh(
        mesh.geometry,
        new THREE.MeshBasicMaterial({
            color: INK,
            side: THREE.BackSide,
            toneMapped: false,
        })
    );
    outline.scale.setScalar(1 + thickness);
    mesh.add(outline);
    return mesh;
};

const sphereGeo = new THREE.SphereGeometry(1, 16, 12);
const boxGeo = new THREE.BoxGeometry(1, 1, 1);
const cylGeo = new THREE.CylinderGeometry(1, 1, 1, 16);
const coneGeo = new THREE.ConeGeometry(1, 1, 16);

/** Smooth, matte, outline-free look shared by the farm, beach and space scenes. */
export const matte = (
    color: THREE.ColorRepresentation,
    extra: THREE.MeshStandardMaterialParameters = {}
) =>
    new THREE.MeshStandardMaterial({
        color,
        roughness: 1,
        metalness: 0,
        ...extra,
    });

/** Drop-in for `inked` that draws no outline. */
export const plain = <T extends THREE.Mesh>(mesh: T, _thickness?: number): T =>
    mesh;

export const ball = (
    mat: THREE.Material,
    rx: number,
    ry = rx,
    rz = rx
): THREE.Mesh => {
    const m = new THREE.Mesh(sphereGeo, mat);
    m.scale.set(rx, ry, rz);
    return m;
};

export const box = (
    mat: THREE.Material,
    w: number,
    h: number,
    d: number
): THREE.Mesh => {
    const m = new THREE.Mesh(boxGeo, mat);
    m.scale.set(w, h, d);
    return m;
};

/** A cylinder of radius `r` and height `h`, standing on y = 0 of its own origin. */
export const cyl = (mat: THREE.Material, r: number, h: number): THREE.Mesh => {
    const m = new THREE.Mesh(cylGeo, mat);
    m.scale.set(r, h, r);
    return m;
};

export const cone = (mat: THREE.Material, r: number, h: number): THREE.Mesh => {
    const m = new THREE.Mesh(coneGeo, mat);
    m.scale.set(r, h, r);
    return m;
};

export const at = <T extends THREE.Object3D>(
    o: T,
    x: number,
    y: number,
    z: number
): T => {
    o.position.set(x, y, z);
    return o;
};

export interface WingPalette {
    /** Coverts: the small overlapping feathers on top of the arm. */
    cover: THREE.ColorRepresentation;
    /** Secondaries and the root of the primaries. */
    flight: THREE.ColorRepresentation;
    /** The ends of the primaries (a gull's black wingtips). */
    tip: THREE.ColorRepresentation;
}

interface Feather {
    /** Distance out along the wing from the group origin, before mirroring. */
    x: number;
    z: number;
    len: number;
    wid: number;
    /** Radians from straight out toward the trailing edge. */
    ang: number;
    y: number;
    c0: THREE.Color;
    c1: THREE.Color;
}

// Half-width profile of a feather, base to tip: narrow quill, belly, rounded end.
const FEATHER_OUTLINE: [number, number][] = [
    [0, -0.12],
    [0.2, -0.38],
    [0.45, -0.5],
    [0.7, -0.38],
    [0.9, -0.18],
    [1, 0],
    [0.9, 0.18],
    [0.7, 0.38],
    [0.45, 0.5],
    [0.2, 0.38],
    [0, 0.12],
];

/** One mesh for a whole row-set of feathers, each a flat leaf with a base-to-tip colour fade. */
const featherMesh = (
    mat: THREE.Material,
    side: number,
    feathers: Feather[]
): THREE.Mesh => {
    const pos: number[] = [];
    const col: number[] = [];
    const tmp = new THREE.Color();
    feathers.forEach((f) => {
        const dx = side * Math.cos(f.ang);
        const dz = -Math.sin(f.ang);
        const vert = (u: number, v: number) => {
            pos.push(
                f.x * side + dx * u * f.len - dz * v * f.wid,
                f.y,
                f.z + dz * u * f.len + dx * v * f.wid
            );
            tmp.copy(f.c0).lerp(f.c1, u);
            col.push(tmp.r, tmp.g, tmp.b);
        };
        const n = FEATHER_OUTLINE.length;
        for (let i = 0; i < n; i++) {
            const [u0, v0] = FEATHER_OUTLINE[i];
            const [u1, v1] = FEATHER_OUTLINE[(i + 1) % n];
            vert(0.4, 0);
            vert(u0, v0);
            vert(u1, v1);
        }
    });
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    geo.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
    const up = new Float32Array(pos.length);
    for (let i = 1; i < up.length; i += 3) up[i] = 1;
    geo.setAttribute('normal', new THREE.BufferAttribute(up, 3));
    return new THREE.Mesh(geo, mat);
};

/**
 * A bird wing built the way a real one is: an inner arm of secondaries under rows of coverts, and a
 * hand of fanned primaries that bends at the wrist. `shoulder` hinges at the body, `tip` at the wrist.
 * `mat` must have vertexColors and DoubleSide set. Spreads along local x, trailing edge toward -z.
 */
export const makeWing = (
    side: number,
    pal: WingPalette,
    mat: THREE.Material
) => {
    const cover = new THREE.Color(pal.cover);
    const flight = new THREE.Color(pal.flight);
    const tipC = new THREE.Color(pal.tip);
    const flightEnd = flight.clone().lerp(tipC, 0.3);
    const WRIST = 0.7;

    const inner: Feather[] = [];
    for (let i = 0; i < 6; i++) {
        const x = 0.06 + i * 0.11;
        const ang = 1.38 - i * 0.04;
        inner.push({
            x,
            z: -0.07,
            len: 0.3 - i * 0.01,
            wid: 0.15,
            ang,
            y: 0,
            c0: flight,
            c1: flightEnd,
        });
        inner.push({
            x,
            z: -0.03,
            len: 0.17,
            wid: 0.14,
            ang,
            y: 0.01,
            c0: cover,
            c1: cover,
        });
    }
    for (let i = 0; i < 5; i++) {
        inner.push({
            x: 0.1 + i * 0.12,
            z: 0.02,
            len: 0.12,
            wid: 0.12,
            ang: 1.3,
            y: 0.02,
            c0: cover,
            c1: cover,
        });
    }
    for (let i = 0; i < 4; i++) {
        inner.push({
            x: 0.12 + i * 0.15,
            z: 0.07,
            len: 0.08,
            wid: 0.1,
            ang: 1.2,
            y: 0.03,
            c0: cover,
            c1: cover,
        });
    }

    const lens = [0.26, 0.31, 0.35, 0.37, 0.37, 0.34, 0.28];
    const angs = [1.05, 0.85, 0.66, 0.48, 0.32, 0.18, 0.05];
    const hand: Feather[] = [];
    lens.forEach((len, i) => {
        hand.push({
            x: i * 0.025,
            z: 0.03,
            len,
            wid: 0.1,
            ang: angs[i],
            y: 0,
            c0: flight,
            c1: tipC,
        });
    });
    angs.slice(0, 5).forEach((ang, i) => {
        hand.push({
            x: i * 0.04,
            z: 0.04,
            len: 0.15,
            wid: 0.09,
            ang,
            y: 0.01,
            c0: cover,
            c1: cover,
        });
    });

    const shoulder = new THREE.Group();
    const tip = new THREE.Group();
    tip.position.set(side * WRIST, 0, 0);
    shoulder.add(featherMesh(mat, side, inner));
    tip.add(featherMesh(mat, side, hand));
    shoulder.add(tip);
    return { shoulder, tip, side };
};

export const radialTexture = (
    stops: [number, string][],
    size = 128
): THREE.CanvasTexture => {
    const c = document.createElement('canvas');
    c.width = size;
    c.height = size;
    const g = c.getContext('2d');
    if (g) {
        const grad = g.createRadialGradient(
            size / 2,
            size / 2,
            0,
            size / 2,
            size / 2,
            size / 2
        );
        stops.forEach(([o, col]) => grad.addColorStop(o, col));
        g.fillStyle = grad;
        g.fillRect(0, 0, size, size);
    }
    const tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
};

/** A soft additive glow, e.g. around the sun. */
export const glow = (
    color: string,
    size: number,
    opacity = 1
): THREE.Sprite => {
    const s = new THREE.Sprite(
        new THREE.SpriteMaterial({
            map: radialTexture([
                [0, color],
                [0.35, color.replace(/[\d.]+\)$/, '0.35)')],
                [1, color.replace(/[\d.]+\)$/, '0)')],
            ]),
            blending: THREE.AdditiveBlending,
            depthWrite: false,
            transparent: true,
            toneMapped: false,
            opacity,
        })
    );
    s.scale.set(size, size, 1);
    return s;
};

/** The sky: a huge inside-out sphere painted with a vertical gradient. `top` is overhead, `horizon` is eye level. */
export const skyDome = (
    top: THREE.ColorRepresentation,
    mid: THREE.ColorRepresentation,
    horizon: THREE.ColorRepresentation
): THREE.Mesh => {
    const c = document.createElement('canvas');
    c.width = 4;
    c.height = 512;
    const g = c.getContext('2d');
    if (g) {
        const grad = g.createLinearGradient(0, 0, 0, 512);
        grad.addColorStop(0, new THREE.Color(top).getStyle());
        grad.addColorStop(0.3, new THREE.Color(top).getStyle());
        grad.addColorStop(0.43, new THREE.Color(mid).getStyle());
        grad.addColorStop(0.5, new THREE.Color(horizon).getStyle());
        grad.addColorStop(1, new THREE.Color(horizon).getStyle());
        g.fillStyle = grad;
        g.fillRect(0, 0, 4, 512);
    }
    const tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    const dome = new THREE.Mesh(
        new THREE.SphereGeometry(400, 24, 16),
        new THREE.MeshBasicMaterial({
            map: tex,
            side: THREE.BackSide,
            depthWrite: false,
            toneMapped: false,
            fog: false,
        })
    );
    dome.renderOrder = -10;
    return dome;
};

/** A puffy toon cloud: a few overlapping spheres with a flat bottom. */
export const cloud = (mat: THREE.Material, seed: number): THREE.Group => {
    const r = rng(seed);
    const g = new THREE.Group();
    const n = 5;
    for (let i = 0; i < n; i++) {
        const mid = 1 - Math.abs(i - (n - 1) / 2) / ((n - 1) / 2);
        const puff = ball(mat, 0.9 + mid * 0.7 + r() * 0.3);
        puff.position.set((i - (n - 1) / 2) * 1.15, mid * 0.35, r() * 0.4);
        g.add(puff);
    }
    g.scale.y = 0.85;
    return g;
};

/** Drifting clouds that wrap around. Returns the tick to register. */
export const addClouds = (
    root: THREE.Object3D,
    mat: THREE.Material,
    count: number,
    seed: number,
    area: { x: number; yMin: number; yMax: number; zMin: number; zMax: number }
): ((t: number, dt: number) => void) => {
    const r = rng(seed);
    const items: { c: THREE.Group; speed: number }[] = [];
    for (let i = 0; i < count; i++) {
        const c = cloud(mat, seed + i * 7);
        const s = 3 + r() * 5;
        c.scale.multiplyScalar(s);
        c.position.set(
            (r() * 2 - 1) * area.x,
            area.yMin + r() * (area.yMax - area.yMin),
            -(area.zMin + r() * (area.zMax - area.zMin))
        );
        root.add(c);
        items.push({ c, speed: 0.25 + r() * 0.5 });
    }
    return (_t, dt) => {
        items.forEach(({ c, speed }) => {
            c.position.x += speed * dt;
            if (c.position.x > area.x + 30) c.position.x = -area.x - 30;
        });
    };
};

/** A shell of stars. Two layers twinkle out of step. */
export const starField = (
    count: number,
    radius: number,
    seed: number,
    opts: { upperOnly?: boolean; size?: number } = {}
): THREE.Points => {
    const r = rng(seed);
    const pos = new Float32Array(count * 3);
    const col = new Float32Array(count * 3);
    const tints = [
        new THREE.Color(0xffffff),
        new THREE.Color(0xcfe0ff),
        new THREE.Color(0xfff0c0),
        new THREE.Color(0xffd0e8),
    ];
    for (let i = 0; i < count; i++) {
        const u = r() * 2 - 1;
        const a = r() * Math.PI * 2;
        const y = opts.upperOnly ? Math.abs(u) * 0.8 + 0.1 : u;
        const s = Math.sqrt(1 - y * y);
        pos[i * 3] = Math.cos(a) * s * radius;
        pos[i * 3 + 1] = y * radius;
        pos[i * 3 + 2] = Math.sin(a) * s * radius;
        const c = tints[Math.floor(r() * tints.length)];
        col[i * 3] = c.r;
        col[i * 3 + 1] = c.g;
        col[i * 3 + 2] = c.b;
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.setAttribute('color', new THREE.BufferAttribute(col, 3));
    const points = new THREE.Points(
        geo,
        new THREE.PointsMaterial({
            size: opts.size ?? 2.4,
            sizeAttenuation: false,
            vertexColors: true,
            transparent: true,
            depthWrite: false,
            toneMapped: false,
        })
    );
    points.renderOrder = -9;
    return points;
};

export const disposeTree = (root: THREE.Object3D) => {
    root.traverse((o) => {
        const m = o as THREE.Mesh;
        m.geometry?.dispose?.();
        const mats = Array.isArray(m.material)
            ? m.material
            : m.material
              ? [m.material]
              : [];
        mats.forEach((mat) => {
            (mat as THREE.MeshBasicMaterial).map?.dispose?.();
            mat.dispose();
        });
    });
};

export interface BackdropKit {
    scene: THREE.Scene;
    root: THREE.Group;
    ticks: ((t: number, dt: number) => void)[];
    finish: (hideShadow?: boolean) => Backdrop;
}

/** The scene, its lights and the list of per-frame animations every backdrop builds on. */
export const createKit = (lights: {
    sky: THREE.ColorRepresentation;
    ground: THREE.ColorRepresentation;
    hemi: number;
    sun: THREE.ColorRepresentation;
    sunIntensity: number;
    sunPos?: [number, number, number];
}): BackdropKit => {
    const scene = new THREE.Scene();
    const root = new THREE.Group();
    scene.add(root);
    scene.add(
        new THREE.HemisphereLight(lights.sky, lights.ground, lights.hemi)
    );
    const sun = new THREE.DirectionalLight(lights.sun, lights.sunIntensity);
    sun.position.set(...(lights.sunPos ?? [6, 12, 8]));
    scene.add(sun);
    const ticks: BackdropKit['ticks'] = [];
    return {
        scene,
        root,
        ticks,
        finish: (hideShadow = false) => ({
            scene,
            root,
            hideShadow,
            update: (t, dt) => ticks.forEach((f) => f(t, dt)),
            dispose: () => disposeTree(scene),
        }),
    };
};

/** A big flat ground disc at the tomato's feet. */
export const groundPlane = (
    color: THREE.ColorRepresentation,
    zFront: number,
    zBack: number,
    width = 400
): THREE.Mesh => {
    const m = new THREE.Mesh(
        new THREE.PlaneGeometry(width, zFront - zBack),
        toon(color)
    );
    m.rotation.x = -Math.PI / 2;
    m.position.set(0, GROUND_Y, (zFront + zBack) / 2);
    return m;
};
